import {
  WbButton,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbLogsViewer,
  WbAlert,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@mainstage/core-plugin-api';
import {
  Box,
  CircularProgress,
  Step,
  StepButton,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import SubjectIcon from '@material-ui/icons/Subject';
import Alert from '@material-ui/lab/Alert';
import { useEffect, useRef, useState } from 'react';
import { panelCatalogApiRef } from '../../api';
import { QuickDeployProgress } from '../../apis/models/quickDeploy';

const steps = [
  'Prepare for deploy',
  'Create or update draft release',
  'Deploy submission',
];

function stepIndex(step: QuickDeployProgress['currentStep']): number {
  switch (step) {
    case 'accepted':
    case 'prepare-deploy':
      return 0;
    case 'creating-draft':
    case 'updating-draft':
      return 1;
    case 'deploying':
    case 'completed':
    case 'failed':
      return 2;
    default:
      return -1;
  }
}

export function QuickDeployStepper({
  projectUrn,
  operationId,
  onFinished,
}: {
  projectUrn: string;
  operationId: string;
  onFinished: (progress: QuickDeployProgress) => void;
}): JSX.Element {
  const api = useApi(panelCatalogApiRef);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [updates, setUpdates] = useState<QuickDeployProgress[]>([]);
  const [streamError, setStreamError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const [selectedStep, setSelectedStep] = useState<number>();
  const finished = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    setStreamError(undefined);
    api
      .watchQuickDeploy(
        projectUrn,
        operationId,
        update => {
          if (controller.signal.aborted) return;
          setUpdates(current =>
            current.some(
              previous =>
                previous.timestamp === update.timestamp &&
                previous.currentStep === update.currentStep &&
                previous.message === update.message &&
                previous.error === update.error &&
                previous.resultingVersion === update.resultingVersion,
            )
              ? current
              : [...current, update].slice(-2000),
          );
          if (
            !finished.current &&
            ['completed', 'failed'].includes(update.currentStep)
          ) {
            finished.current = true;
            onFinished(update);
          }
        },
        { signal: controller.signal },
      )
      .catch(error => {
        if (!controller.signal.aborted && !finished.current) {
          setStreamError(
            error instanceof Error
              ? error.message
              : 'Quick Deploy monitoring unavailable',
          );
        }
      });
    return () => controller.abort();
  }, [api, projectUrn, operationId, attempt, onFinished]);

  const latest = updates[updates.length - 1];
  const succeeded = latest?.currentStep === 'completed';
  const failed = latest?.currentStep === 'failed';
  const groupedLogs: QuickDeployProgress[][] = steps.map(() => []);
  let activeStep = 0;
  for (const update of updates) {
    const index = stepIndex(update.currentStep);
    if (index >= 0) activeStep = index;
    groupedLogs[activeStep].push(update);
  }
  const displayedLogs =
    selectedStep === undefined ? updates : groupedLogs[selectedStep];
  const text = displayedLogs
    .map(
      update =>
        `${update.timestamp} ${update.currentStep}: ${update.message}${
          update.error && update.error !== update.message
            ? ` (${update.error})`
            : ''
        }`,
    )
    .join('\n');

  const getStepIcon = (index: number) => {
    if (succeeded || index < activeStep)
      return (
        <CheckCircleIcon
          htmlColor={theme.palette.success.main}
          titleAccess={`${steps[index]} completed`}
        />
      );
    if (index !== activeStep) return undefined;
    if (failed)
      return (
        <CancelIcon color="error" titleAccess={`${steps[index]} failed`} />
      );
    if (streamError)
      return <PauseCircleOutlineIcon titleAccess="Monitoring unavailable" />;
    return (
      <CircularProgress size={22} aria-label={`${steps[index]} in progress`} />
    );
  };

  return (
    <Box>
      {failed && (
        <WbAlert
          message={{
            type: 'error',
            title: 'Error',
            content: latest.error || latest.message,
          }}
        />
      )}
      {succeeded && (
        <WbAlert
          message={{
            type: 'success',
            title: 'Success',
            content: `${latest.message}${
              latest.resultingVersion
                ? ` Version: ${latest.resultingVersion}.`
                : ''
            }`,
          }}
        />
      )}
      {streamError && (
        <Alert
          severity="warning"
          action={
            <WbButton
              label="Retry monitoring"
              variant="text"
              icon={<RefreshIcon />}
              onClick={() => setAttempt(current => current + 1)}
            />
          }
        >
          Monitoring disconnected. Deployment status is unknown. {streamError}
        </Alert>
      )}
      <Box style={{ height: smallScreen ? '70vh' : '55vh', minHeight: 360 }}>
        <WbDockableGroup direction={smallScreen ? 'vertical' : 'horizontal'}>
          <WbDockablePanel
            title="Task Progress"
            defaultSize={30}
            minSize={20}
            primary
            headerBorderBottom
          >
            <Stepper
              nonLinear
              activeStep={succeeded ? steps.length : activeStep}
              orientation="vertical"
            >
              {steps.map((label, index) => (
                <Step key={label} completed={succeeded || index < activeStep}>
                  <StepButton
                    aria-current={
                      !failed && !succeeded && index === activeStep
                        ? 'step'
                        : undefined
                    }
                    aria-pressed={selectedStep === index}
                    disabled={groupedLogs[index].length === 0}
                    onClick={() =>
                      setSelectedStep(current =>
                        current === index ? undefined : index,
                      )
                    }
                    icon={getStepIcon(index)}
                  >
                    <Typography
                      color={
                        failed && index === activeStep ? 'error' : 'inherit'
                      }
                    >
                      {label}
                    </Typography>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </WbDockablePanel>
          <WbDockableResizeHandle
            direction={smallScreen ? 'vertical' : 'horizontal'}
          />
          <WbDockablePanel
            title={
              selectedStep === undefined ? 'Progress Logs' : steps[selectedStep]
            }
            headerBorderBottom
          >
            <Box
              padding={2}
              height="100%"
              display="flex"
              flexDirection="column"
              minHeight={0}
            >
              {selectedStep !== undefined && (
                <WbButton
                  label="All logs"
                  variant="text"
                  icon={<SubjectIcon />}
                  onClick={() => setSelectedStep(undefined)}
                />
              )}
              <Box flex={1} minHeight={0} overflow="hidden">
                <WbLogsViewer text={text || 'Waiting for progress...'} />
              </Box>
              {updates.length >= 2000 && (
                <Typography variant="caption">
                  Showing the latest 2000 progress messages.
                </Typography>
              )}
            </Box>
          </WbDockablePanel>
        </WbDockableGroup>
      </Box>
    </Box>
  );
}
